import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { LogResponseSchema } from "../response/LogResponse";

const API_VERSION = "v3";

interface FilterProps {
    all?: number;
    model_id?: string;
    property_id?: string | null;
    /** Log History tab (per reservation) — see LogController@index. */
    booking_room_id?: string;
    correlation_id?: string;
    /** Module/domain filter — one or more exact model_type class strings. */
    model_type?: string | string[];
}

interface Props {
    filters?: FilterProps,
    search?: string,
    limit?: number,
    queryKey?: string,
    page?: number,
    include?: string,
}

const useLog = (query: Props) => {
    return useQuery({
        queryKey: [
            'log',
            query.filters?.all ? 'all' : '',
            ...Object.entries(query.filters ?? {})
                .filter(([key, value]) => key !== 'all' && value !== undefined && value !== null && value !== "")
                .map(([key, value]) => `${key}:${value}`),
            query.search,
            query.queryKey,
            query.page,
            query.limit,
            query.include,
        ],
        queryFn: async () => {
            try {
                // `all` and `filter` used to be mutually exclusive here — passing
                // `filters.all` silently dropped every other filter (model_id,
                // booking_room_id, ...), which the Log History tab needs together
                // with `all` (fetch every row for one reservation, unpaginated).
                const params = {
                    filter: Object.fromEntries(
                        Object.entries(query.filters ?? {}).filter(([key, value]) => key !== "all" && value !== undefined && value !== null && value !== "")
                    ),
                    search: query.search || "",
                    include: query.include,
                    ...(query.filters?.all ? { all: 1 } : { limit: query.limit ?? 10, page: query.page }),
                };

                const response = await privateApi.get(`/${API_VERSION}/log`, { params });

                // Validasi response
                const validationResult = LogResponseSchema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid Log data format");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch Log`, error);
                throw error;
            }
        },
        retry: import.meta.env.VITE_MAX_RETRY,
        refetchOnWindowFocus: false,
    });
};

export default useLog;